import type { MonitoringTraysView, TrayName } from '@ctcells/api'
import type { ModuleSnapshot } from '@ctcells/contract'

import { deviceKeyOfModuleType, findModule, type MonitoringDeviceKey } from './monitoringModel.ts'
import {
  handoffSpots,
  ROBOT_STATIONS,
  robotRun,
  type HandoffSpot,
  type RobotRun,
  type RobotStationKey,
} from './robotTransferModel.ts'

/**
 * **6축 로봇 가로 라인의 «지금»**(eric 2026-10-08 「가로로 원심분리기 · 어셈블러 · 염색기 줄 넣어주고 실제 6축 로봇이 왔다갔다하는 것을
 * 표현해줘. step 에 맞게. 해당 모듈 부분에서 트레이 넣고 빼는 것도 스텝에 맞게」).
 *
 * 서버가 주행축 위치(`rail_stop`)를 발행하지 않으므로(dev2 2026-10-06 · 10-08 실측 — telemetry 는 `grip_drop_policy` 뿐) 로봇의 자리는
 * **서버 사실 세 가지**로만 읽는다 — 짐작하지 않고, 사실이 없으면 `undefined`(화면은 마지막 자리를 그대로 두거나 「위치 미발행」을 적는다):
 *
 * | 사실(출처) | 읽는 법 |
 * | --- | --- |
 * | 6축 `current_execution.sequence`(`module_status` 1Hz) | `grip` 집는 중 · `release` 놓는 중 · `move_aux_axis` 주행 · `move_to_module` 접근 · `home` 파킹 복귀 |
 * | 모듈 상태(`TRAY_UNLOADING` · `WAITING_FOR_TRAY_UNLOAD` ↔ 반출 · `TRAY_LOADING` ↔ 반입) | 집는 곳 = 반출 중인 모듈 · 놓는 곳 = 반입 중인 모듈 |
 * | 트레이 뷰 `trays[].location`(`in_transit` 이면 서버 핸드오프 행 · `module_type` 은 그 공정 단계의 자리) | 쥐고 가는 트레이가 «향하는» 모듈 |
 * | 6축 `grips[].state === 'holding'` | 트레이를 쥐고 있는가(이송 중) |
 *
 * dev2 2026-10-07 15:07~15:09 실측(이송 한 번 = 6축 실행 다섯: 접근 → 집기 → 주행 → 놓기 → 복귀)이 이 표의 근거다.
 * 🔴 모듈 핸드오프 자리의 트레이 유무는 `handoff_occupancy`(서버 사실)로만 그린다.
 */
export type RobotLinePlace = RobotStationKey | 'park'
export type RobotLineAction = 'idle' | 'approach' | 'travel' | 'grip' | 'release' | 'home'

export interface RobotLineStationView {
  key: RobotStationKey
  snapshot: ModuleSnapshot | undefined
  /** 그 모듈이 발행한 핸드오프 자리 — `undefined` 미발행 */
  spots: readonly HandoffSpot[] | undefined
  /** 지금 트레이를 내놓는 중(반출)인가 · 받는 중(반입)인가 — 모듈 상태 어휘 그대로 */
  handoff: 'unloading' | 'loading' | undefined
}

/** 트레이 한 장이 지금 어디 있나 — 트레이 뷰 `trays[].location` 그대로(eric 2026-10-08 「현재 트레이가 어디에 위치해 있는지도 표시해줘」) */
export interface RobotLineTray {
  tray: TrayName
  /** 그 자리의 모듈(정거장) — 화면에 없는 종류면 `undefined` */
  station: RobotStationKey | undefined
  /** 서버 자리 이름 원값(`centrifuge.slide_front`) · 꼬리(`slide_front`) */
  pointId: string
  tail: string
  /** 6축이 들고 가는 중(서버 핸드오프 행) */
  inTransit: boolean
  /** 트레이 역할(`centrifuge_output_assembler_input`) — 놓을 자리(거치대 · 슬라이드)를 고른다(`placeSideOf`) */
  role?: string | undefined
}

export interface RobotLineView {
  action: RobotLineAction
  /** 서 있거나 향하는 자리. 사실이 없으면 `undefined` — 화면은 마지막 자리를 유지한다 */
  target: RobotLinePlace | undefined
  /** 지금 다루는 모듈(집기 · 놓기 · 향하는 곳) — 영상 칸이 이 모듈의 카메라를 켠다 */
  station: RobotStationKey | undefined
  carrying: boolean
  /**
   * 들고 간 트레이를 놓을 자리 — 거치대(왼쪽 위) · 슬라이드(`placeSideOf`). 놓기 · 들고 가는 중에만 · 모르면 `undefined`(슬라이드로 선다)
   */
  placeTo: 'holder' | 'slide' | undefined
  /**
   * 집는 자리 — 거치대(왼쪽 위) · 슬라이드. 🔴 집는 걸음(`grip`)에만 · **집는 트레이가 놓여 있던 자리**로 고른다(`gripSideOf`).
   * 6축 실행은 집는 지점을 싣지 않는다(`current_execution` = 시퀀스 이름 · 걸음 수뿐 · 2026-10-21 로컬 실측) — 모르면 `undefined`(화면은 종전 규칙)
   */
  gripFrom: 'holder' | 'slide' | undefined
  run: RobotRun | undefined
  stations: RobotLineStationView[]
  /** 트레이 세 장의 자리(트레이 뷰가 없으면 빈 목록 — 「없다」가 아니라 「모른다」) */
  trays: RobotLineTray[]
}

const UNLOADING: ReadonlySet<string> = new Set(['TRAY_UNLOADING', 'WAITING_FOR_TRAY_UNLOAD'])
const LOADING: ReadonlySet<string> = new Set(['TRAY_LOADING'])

const ACTION_OF: Readonly<Record<string, RobotLineAction>> = {
  grip: 'grip',
  release: 'release',
  place_held: 'release',
  move_aux_axis: 'travel',
  move_to_module: 'approach',
  move_to_pose: 'approach',
  home: 'home',
}

function isStation(key: string | undefined): key is RobotStationKey {
  return (ROBOT_STATIONS as readonly string[]).includes(key ?? '')
}

/**
 * 트레이 역할 이름에서 목적지 모듈 — `centrifuge_input` → 원심분리기 · `centrifuge_output_assembler_input` → 어셈블러 · `assembler_output_staining` → 염색기.
 * 🔴 이름을 해석할 뿐 길을 짓지 않는다 — 꼴이 다르면 `undefined`
 */
export function destinationOfRole(
  role: string | null | undefined,
): MonitoringDeviceKey | undefined {
  if (typeof role !== 'string') return undefined
  const after = /_output_([a-z0-9]+)/.exec(role)?.[1]
  const only = /^([a-z0-9]+)_input$/.exec(role)?.[1]
  return deviceKeyOfModuleType(after ?? only)
}

/**
 * 트레이 역할 이름의 두 끝 — `centrifuge_output_assembler_input` → [원심, 어셈블러] · `assembler_output_staining` → [어셈블러, 염색기] ·
 * `centrifuge_input` → [원심]. 🔴 방향은 이름이 정하지 않는다 — 준비 단계에는 같은 L 이 염색기 → 어셈블러로 «거꾸로» 간다(eric 2026-10-08)
 */
export function endpointsOfRole(role: string | null | undefined): MonitoringDeviceKey[] {
  if (typeof role !== 'string') return []
  const pair = /^([a-z0-9]+)_output_([a-z0-9]+)/.exec(role)
  const names = pair !== null ? [pair[1], pair[2]] : [/^([a-z0-9]+)_input$/.exec(role)?.[1]]
  return names
    .map((name) => deviceKeyOfModuleType(name))
    .filter((key): key is MonitoringDeviceKey => key !== undefined)
}

/**
 * 걸음을 따라 들고 다니는 기억 — 부르는 쪽(`RobotLine`)이 다음 프레임에 다시 넘긴다.
 * 🔴 **«쥐었다»는 `grip` 걸음(또는 파지 `holding`)이 정한다 — 트레이 뷰의 `in_transit` 이 아니다.** 서버는 이송을 «시작»하자마자(6축이 아직 빈손으로
 *    접근하는 중) 그 트레이를 `in_transit` 으로 적는다(dev2 2026-10-08 16:24:37 실측 — `move_to_module` · 파지 `at_target` · L `in_transit`).
 *    그걸 «들고 간다»로 읽어 호이스트가 트레이를 단 채 목적지로 먼저 갔다가 집으러 출발지로 돌아왔다
 *    (eric 2026-10-08 「그리퍼에서 A 트레이를 잡고 왔다갔다해… 준비 단계에서 어셈블러 트레이를 들고 왔다갔다해」).
 */
export interface RobotLineTrack {
  gripped?: boolean | undefined
  /** `release`(놓기)를 봤다 — 다음 다른 걸음에서 빈손으로 */
  releasing?: boolean | undefined
  /** 놓은 뒤 파지 판독이 늦게 `holding` 으로 남는 동안 다시 쥔 것으로 읽지 않는다 */
  released?: boolean | undefined
  /** 집은 정거장(출발지) — 목적지를 역할 이름의 «다른 끝»으로 고른다 */
  from?: RobotStationKey | undefined
  /** 집는 자리(거치대 · 슬라이드) — 집는 동안 모듈 점유가 «비었음»으로 바뀌어도 팔이 그 자리에 머문다 */
  fromSide?: 'holder' | 'slide' | undefined
}

const RELEASE_SEQUENCES: ReadonlySet<string> = new Set(['release', 'place_held'])

export function robotLineView(
  robot: ModuleSnapshot | undefined,
  modules: readonly ModuleSnapshot[],
  trays: MonitoringTraysView | undefined,
  stale: boolean,
  track?: RobotLineTrack,
): RobotLineView {
  return robotLineStep(robot, modules, trays, stale, track).view
}

/** 한 프레임 — 지금 그릴 자리와 다음 프레임에 넘길 기억 */
export function robotLineStep(
  robot: ModuleSnapshot | undefined,
  modules: readonly ModuleSnapshot[],
  trays: MonitoringTraysView | undefined,
  stale: boolean,
  track: RobotLineTrack = {},
): { view: RobotLineView; track: RobotLineTrack } {
  const stations: RobotLineStationView[] = ROBOT_STATIONS.map((key) => {
    const snapshot = findModule(modules, key)
    const state = snapshot?.state ?? ''
    return {
      key,
      snapshot,
      spots: handoffSpots(snapshot),
      handoff: UNLOADING.has(state) ? 'unloading' : LOADING.has(state) ? 'loading' : undefined,
    }
  })
  const viewRows: RobotLineTray[] = (trays?.trays ?? []).map((tray) => {
    const key = deviceKeyOfModuleType(tray.location.module_type)
    const prefix = `${tray.location.module_type}.`
    return {
      tray: tray.tray,
      station: isStation(key) ? key : undefined,
      pointId: tray.location.point_id,
      tail: tray.location.point_id.startsWith(prefix)
        ? tray.location.point_id.slice(prefix.length)
        : tray.location.point_id,
      inTransit: tray.location.in_transit,
      role: tray.role,
    }
  })
  const trayRows = settleHolderTrays(viewRows, stations)
  /* 아직 안 쥔 트레이의 «제자리» — 이송 중 표지를 떼고 거치대 사실로 맞춘 자리(`settleHolderTrays`) */
  const restRows = settleHolderTrays(
    viewRows.map((tray) => (tray.inTransit ? { ...tray, inTransit: false } : tray)),
    stations,
  )
  const quiet = robot === undefined || stale || robot.stale === true
  if (quiet)
    return {
      view: {
        action: 'idle',
        target: undefined,
        station: undefined,
        carrying: false,
        placeTo: undefined,
        gripFrom: undefined,
        run: undefined,
        stations,
        trays: trayRows,
      },
      track,
    }

  const run = robotRun(robot)
  const action: RobotLineAction =
    run?.sequence !== undefined ? (ACTION_OF[run.sequence] ?? 'approach') : 'idle'

  const unloading = stations.find((s) => s.handoff === 'unloading')?.key
  const loading = stations.find((s) => s.handoff === 'loading')?.key
  const moving = trays?.trays.find((tray) => tray.location.in_transit)
  /*
   * 🔴 이송 중 트레이가 «향하는» 모듈 — 먼저 트레이의 **역할**(`role` · `assembler_output_staining` 처럼 서버가 길을 적어 둔 이름)에서, 없으면
   *    `location.module_type` 에서. dev2 2026-10-08 실측 — 어셈블러에서 L 을 뺀 직후 프레임에 위치가 아직 어셈블러로 남아 호이스트가 어셈블러
   *    가운데에 섰다(eric 「L 을 뺀 후 염색기 트레이 부분 위치로 가야 되는데 가로 위치가 안 맞잖아」). 역할은 트레이가 바뀌지 않는 한 같다
   */
  /* 이송이 시작된 트레이가 아직 놓인 곳 — 셈한 자리가 아니라 거치대 사실로 맞춘 제자리(첫 이송 전 A 는 어셈블러 · eric 2026-10-21) */
  const movingStation =
    moving === undefined ? undefined : restRows.find((row) => row.tray === moving.tray)?.station
  const sequence = run?.sequence
  const holdingGrip = (robot.grips ?? []).some((grip) => grip.state === 'holding')

  /* ── 기억 — 쥠은 `grip` 걸음에서 · 놓음은 `release` 뒤 다음 걸음에서(`RobotLineTrack` 머리말) ── */
  let { gripped, releasing, released, from, fromSide } = track
  if (!holdingGrip) released = undefined
  if (moving === undefined && !holdingGrip) {
    gripped = undefined
    releasing = undefined
    from = undefined
    fromSide = undefined
  }
  if (gripped === true && sequence !== undefined && RELEASE_SEQUENCES.has(sequence))
    releasing = true
  else if (gripped === true && releasing === true && sequence !== undefined) {
    gripped = undefined
    releasing = undefined
    released = holdingGrip ? true : undefined
    from = undefined
    fromSide = undefined
  }
  if (gripped !== true && (sequence === 'grip' || (holdingGrip && released !== true))) {
    gripped = true
    /* 집는 걸음이면 트레이가 아직 출발지에 있다 — 파지만 보고 잡은 기억(중간에 화면을 연 때)은 자리가 이미 목적지일 수 있어 쓰지 않는다 */
    from = unloading ?? (sequence === 'grip' ? movingStation : undefined)
  }
  const carrying = gripped === true

  /*
   * 🔴 이송 중 트레이가 «향하는» 모듈 — 역할 이름의 두 끝 중 «집은 곳이 아닌» 끝(준비 단계의 거꾸로 가는 L 도 맞게), 없으면 받는 중인 모듈 ·
   *    트레이 자리. dev2 2026-10-08 실측 — 어셈블러에서 L 을 뺀 직후 프레임에 위치가 아직 어셈블러로 남아 호이스트가 어셈블러 가운데에 섰다
   *    (eric 「L 을 뺀 후 염색기 트레이 부분 위치로 가야 되는데 가로 위치가 안 맞잖아」)
   */
  const ends = endpointsOfRole(moving?.role).filter(isStation)
  const other = from === undefined ? undefined : ends.find((end) => end !== from)
  const roleTo = destinationOfRole(moving?.role)
  const transit = other ?? loading ?? (isStation(roleTo) ? roleTo : undefined) ?? movingStation

  let station: RobotStationKey | undefined
  switch (action) {
    case 'grip':
      station = from ?? unloading ?? movingStation
      break
    case 'release':
      station = loading ?? transit
      break
    case 'approach':
    case 'travel':
      /* 빈손 — 집으러 가는 곳(반출 중인 모듈 · 이송이 시작된 트레이가 아직 놓인 곳) */
      station = carrying ? transit : (unloading ?? movingStation ?? loading)
      break
    case 'idle':
      /* 실행 이름이 없어도 모듈이 핸드오프 중이면 그 모듈 앞이다(상태 `HANDOFF`) */
      station =
        robot.state === 'HANDOFF'
          ? carrying
            ? transit
            : (unloading ?? movingStation ?? loading)
          : undefined
      break
    case 'home':
      station = undefined
      break
  }
  const target: RobotLinePlace | undefined = action === 'home' ? 'park' : station
  /* 집는 자리 — 이 프레임에서 고를 수 있으면 새로, 못 고르면(트레이가 이미 빠져 점유가 «비었음») 이 걸음에서 고른 자리를 그대로 */
  if (action === 'grip') {
    const at = stations.find((st) => st.key === station)
    const picked = at === undefined ? undefined : gripSideOf(at, restRows, moving?.tray)
    if (picked !== undefined) fromSide = picked
  }
  const gripFrom = action === 'grip' ? fromSide : undefined
  const next: RobotLineTrack = { gripped, releasing, released, from, fromSide }
  /* 🔴 아직 안 쥔 이송 트레이는 제자리에 그린다 — 서버의 `in_transit` 은 «이송 시작»이지 «포크 위»가 아니다 */
  const shownTrays = carrying ? trayRows : restRows
  const placeTo =
    (action === 'release' || carrying) && station !== undefined
      ? placeSideOf(moving?.role, station)
      : undefined
  return {
    view: {
      action,
      target,
      station,
      carrying,
      placeTo,
      gripFrom,
      run,
      stations,
      trays: shownTrays,
    },
    track: next,
  }
}

/**
 * **들고 간 트레이를 그 정거장의 어디에 놓나** — 거치대(왼쪽 위) · 슬라이드(eric 2026-10-19 「원심분리기 작업 전에.. A 트레이를.. 슬라이드 말고
 * 파란색 쪽에 · 어셈블러 시작하기 전에도 L 트레이를.. 어셈블러의 왼쪽 위에」). 거치대에 서는 트레이는 «그 정거장에서 다음으로 나갈» 트레이다 —
 * 역할 이름의 앞 끝(`centrifuge_output_assembler_input` 의 원심 · `assembler_output_staining` 의 어셈블러)이 그 정거장이면 거치대, 아니면 슬라이드.
 * 서버 이송 기록과 같다(10-19 실측 · 빈 A `assembler.grip_tray_on_slide → centrifuge.grip_tray_on_holder` · 빈 L `staining… → assembler.grip_tray_on_holder` ·
 * A 반출 `centrifuge.grip_tray_on_holder → assembler.grip_tray_on_slide`). 🔴 역할이 없거나 꼴이 다르면 슬라이드(종전 그대로)
 */
export function placeSideOf(
  role: string | null | undefined,
  station: RobotStationKey,
): 'holder' | 'slide' {
  if (typeof role !== 'string' || !/_output_/.test(role)) return 'slide'
  return endpointsOfRole(role)[0] === station ? 'holder' : 'slide'
}

/**
 * 정거장 안 트레이 자리 — **왼쪽 슬라이드 열**(뒷문 ↔ 앞문을 오가는 한 슬라이드 · 트레이는 후면 · 전면 어느 쪽에 있다) + **오른쪽 거치대**(6축이
 * 꺼내 가는 자리). eric 2026-10-08 「원심분리기는 C 트레이가 왼쪽 점선 상에, 6축은 원심분리기 오른쪽 점선 부분(A)… 어셈블러는 점선 왼쪽에 A 가 움직여,
 * L 은 어셈블러 오른쪽 점선과 염색기 트레이만 왔다갔다」. 염색기는 슬라이드만 있다.
 *
 * 재료는 모듈 `handoff_occupancy`(발행 자리 · 찼음)와 트레이 뷰 `trays[].location`(어느 트레이가 어느 자리에)뿐 — 짐작하지 않는다.
 */
export interface RobotLineSlide {
  /** 모듈이 슬라이드 자리를 발행했는가(`slide_rear`) */
  published: boolean
  /** 모듈이 「후면 찼음」이라 했다 */
  occupiedRear: boolean
  /** 여기 있는 트레이(트레이 뷰) · 그 트레이의 자리(후면 · 전면) */
  tray: RobotLineTray | undefined
  pos: 'rear' | 'front' | undefined
}
export interface RobotLineHolder {
  published: boolean
  occupied: boolean
  tray: RobotLineTray | undefined
}
export interface RobotLineLayout {
  slide: RobotLineSlide
  /** 거치대 — 발행도 트레이도 없으면 `undefined`(염색기) */
  holder: RobotLineHolder | undefined
  /** 자리 발행도 트레이 뷰도 없다 — 그 까닭은 `note` */
  unknown: boolean
  /**
   * 모를 때의 까닭 — 모듈 없음 · 자리 점유 미발행 · 발행한 자리 0개(종전 「핸드오프 자리」 표의 사유 칸과 같은 셋 · 그 표는 걷었다 eric 2026-10-21).
   * 🔴 셋 다 «빔»으로 접지 않는다 — 그림은 빈 자리 점선 대신 이 글자를 적는다
   */
  note: 'missing' | 'unpublished' | 'none' | undefined
}

/**
 * 자리 이름 두 벌 — 🔴 v0.9.4 부터 모듈 자리 점유(`handoff_occupancy`)는 6축 파지점 이름(`grip_tray_on_holder` · `grip_tray_on_slide`)을 내고, 트레이 뷰
 * 위치는 종전 이름(`tray-1` · `slide_rear`)을 그대로 낸다(서버 `tray_view.py` · 2026-10-19 실측). 한쪽만 보면 거치대가 «미발행»으로 사라져 6축이 늘
 * 슬라이드에 놓는 그림이 됐다(eric 2026-10-19 「저쪽 트레이가 아니라.. 파란색의 공간에」)
 */
const HOLDER_TAILS: ReadonlySet<string> = new Set(['tray-1', 'grip_tray_on_holder'])
const SLIDE_SPOT_TAILS: ReadonlySet<string> = new Set(['slide_rear', 'grip_tray_on_slide'])

/**
 * **거치대 자리는 모듈의 점유(사실)가 트레이 뷰의 셈(derived)보다 앞선다**(eric 2026-10-21 「처음 운송 로봇 선택시… A 트레이는 C 모듈 쪽의 왼쪽 위에
 * 미리 옮겨져 있어」).
 *
 * 서버 트레이 뷰의 자리(`trays[].location`)는 배치 국면에서 «셈한» 값이다 — 배치가 시작되면(`started`) 원심이 끝나기 전 국면 전체를 A = `centrifuge.tray-1`
 * (거치대)로 적는다(ctcells-core `tray_view.py` `tray_location` 표 · `source: derived`). 그런데 첫 이송(빈 A 를 어셈블러 슬라이드 → 원심 거치대)이 끝나기
 * 전에는 A 가 아직 어셈블러에 있다 — 모듈은 그 사실을 `handoff_occupancy` 로 «원심 거치대 비었음 · 어셈블러 슬라이드 찼음»이라 말한다.
 * L(`assembler.tray-1`)도 같은 꼴이다.
 *
 * ⇒ 트레이 뷰가 **거치대**에 세운 트레이인데 그 모듈이 거치대를 «비었음»으로 발행했고, **그 트레이 역할 이름의 다른 끝**(`centrifuge_output_assembler_input`
 *    의 어셈블러)의 슬라이드가 «찼음»으로 발행돼 있으며 다른 트레이가 그 슬라이드를 차지하지 않았으면 — 두 사실이 맞물려 «아직 출발 슬라이드에 있다»고
 *    말할 때만 — 그 슬라이드로 옮긴다.
 * 🔴 **그 밖에는 트레이 뷰 자리(거치대) 그대로다 — «어디에도 안 그림»으로 떨어지지 않는다**(eric 2026-10-21 「반입을 했으면.. 왼쪽 위 A 트레이에..
 *    트레이가 들어가야지… 왼쪽 위에.. 안 들어가」). 6축이 거치대에 놓은 뒤 반입 중(`TRAY_LOADING`)에는 모듈의 거치대 점유가 한동안 «비었음»으로 남는다
 *    (로컬 2026-10-21 10:16 실측 — 원심 `TRAY_LOADING` · 거치대 0 · 어셈블러 슬라이드 0). 출발 슬라이드가 이미 비었으면 트레이는 떠난 것이다.
 *    거치대가 미발행이거나 «찼음»이어도 그대로. 이송 중(`in_transit`) 트레이 · 슬라이드 자리는 건드리지 않는다
 *    (슬라이드 점유는 후면 하나만 발행돼 «비었음»이 «전면에 있다»일 수 있다).
 */
export function settleHolderTrays(
  rows: readonly RobotLineTray[],
  stations: readonly RobotLineStationView[],
): RobotLineTray[] {
  const spotOf = (key: RobotStationKey | undefined, tails: ReadonlySet<string>) =>
    stations.find((st) => st.key === key)?.spots?.find((spot) => tails.has(spot.tail))
  return rows.map((tray) => {
    if (tray.inTransit || tray.station === undefined || !HOLDER_TAILS.has(tray.tail)) return tray
    const holder = spotOf(tray.station, HOLDER_TAILS)
    if (holder === undefined || holder.occupied) return tray
    const other = endpointsOfRole(tray.role)
      .filter(isStation)
      .find((end) => end !== tray.station)
    const slide = spotOf(other, SLIDE_SPOT_TAILS)
    const taken = rows.some(
      (row) => row.station === other && !row.inTransit && row.tail.startsWith('slide'),
    )
    if (other === undefined || slide === undefined || !slide.occupied || taken) return tray
    return { ...tray, station: other, pointId: slide.pointId, tail: 'slide_rear' }
  })
}

/**
 * **집는 자리**(eric 2026-10-21 「A 트레이가.. 오른쪽 슬라이더에 잇는데.. 거기서 빼야지. 왜 슬라이더 왼쪽 위에 있는.. 점선.. 에서.. 트레이를 빼?」).
 * 6축은 집는 지점을 알려 주지 않는다(`current_execution` 에 `point_id` 없음 · 파지 `tray_key` 도 비어 옴 · 2026-10-21 로컬 실측) — 그래서
 * **집는 트레이가 지금 놓인 자리**로 고른다: ① 이송이 시작된 트레이(`in_transit`)의 제자리(`settleHolderTrays` 로 맞춘 자리)가 이 정거장이면 그 자리,
 * ② 아니면 이 정거장에서 트레이가 있는 쪽 — 거치대가 찼으면 거치대, 슬라이드에 트레이가 있거나 후면이 찼으면 슬라이드. 둘 다 아니면 `undefined`(모른다).
 * 종전에는 거치대가 «있기만» 하면 늘 거치대 위로 갔다 — 어셈블러 슬라이드에서 A 를 빼는데 팔이 빈 거치대 위에 섰다.
 */
export function gripSideOf(
  station: RobotLineStationView,
  trays: readonly RobotLineTray[],
  tray: TrayName | undefined,
): 'holder' | 'slide' | undefined {
  const mine = trays.find((row) => row.tray === tray && row.station === station.key)
  if (mine !== undefined && !mine.inTransit) {
    if (HOLDER_TAILS.has(mine.tail)) return 'holder'
    if (mine.tail.startsWith('slide')) return 'slide'
  }
  const layout = stationLayout(station, trays)
  if (layout.holder?.occupied === true) return 'holder'
  if (layout.slide.tray !== undefined || layout.slide.occupiedRear) return 'slide'
  return undefined
}

export function stationLayout(
  station: RobotLineStationView,
  trays: readonly RobotLineTray[],
): RobotLineLayout {
  const here = trays.filter((tray) => tray.station === station.key && !tray.inTransit)
  const spots = station.spots ?? []
  const rearSpot = spots.find((spot) => SLIDE_SPOT_TAILS.has(spot.tail))
  const holderSpot = spots.find((spot) => HOLDER_TAILS.has(spot.tail))
  const slideTray = here.find((tray) => tray.tail.startsWith('slide'))
  const holderTray = here.find((tray) => HOLDER_TAILS.has(tray.tail))
  const slide: RobotLineSlide = {
    published: rearSpot !== undefined,
    occupiedRear: rearSpot?.occupied === true,
    tray: slideTray,
    pos: slideTray === undefined ? undefined : slideTray.tail === 'slide_front' ? 'front' : 'rear',
  }
  const holder: RobotLineHolder | undefined =
    holderSpot === undefined && holderTray === undefined
      ? undefined
      : {
          published: holderSpot !== undefined,
          occupied: holderSpot?.occupied === true || holderTray !== undefined,
          tray: holderTray,
        }
  const note =
    here.length > 0
      ? undefined
      : station.snapshot === undefined
        ? ('missing' as const)
        : station.spots === undefined
          ? ('unpublished' as const)
          : station.spots.length === 0
            ? ('none' as const)
            : undefined
  return { slide, holder, unknown: note !== undefined, note }
}

/**
 * 정거장 문 상태 — 서버 `interlock.doors[] {face, state}` 그대로(염색기 후문이 이걸 낸다 · 다른 모듈은 미발행이면 `null`).
 * 앞문(사람 쪽) · 뒷문(레일 쪽 · 6축이 트레이를 넣고 빼는 문)(eric 2026-10-08 「앞문과 뒷문을 만들어줘」). `null` = 모름 — 열림/닫힘을 가르지 않는다
 */
export function stationDoor(
  snapshot: ModuleSnapshot | undefined,
  face: 'front' | 'rear',
): 'open' | 'closed' | null {
  const doors: unknown = snapshot?.interlock?.doors
  if (!Array.isArray(doors)) return null
  for (const d of doors as unknown[]) {
    if (typeof d !== 'object' || d === null) continue
    const { face: f, state } = d as { face?: unknown; state?: unknown }
    if (f === face && (state === 'open' || state === 'closed')) return state
  }
  return null
}
